import React, { useCallback } from 'react';
import PropTypes from 'prop-types';

import makeStyles from '@mui/styles/makeStyles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import CloudUpload from '@mui/icons-material/CloudUpload';

import CloudDone from '@mui/icons-material/CloudDone';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import Cancel from '@mui/icons-material/Cancel';
import UploadProgressState from './UploadProgressState';

import BorderLinearProgress from './BorderLinearProgress';
import { green } from '@mui/material/colors';

const useStyles = makeStyles((theme) => ({
  defaultIcon: {
    fontSize: 48,
    color: theme.palette.primary.main,
  },
  successIcon: {
    fontSize: 48,
    color: green[500],
  },
  failedIcon: {
    fontSize: 48,
    color: theme.palette.error.main,
  },
  progressText: {
    marginBottom: -14,
  },
  fileNameText: {
    marginTop: -theme.spacing(2),
  },
  errorMessage: {
    marginRight: theme.spacing(1),
  },
}));

const UploadProgressIndicator = (props) => {
  const classes = useStyles();
  const { className, progress, fileName, state, error, onClose } = props;

  const handleRootClick = useCallback((event) => {
    event.stopPropagation();
    event.preventDefault();
  }, []);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  function renderIcon() {
    switch (state) {
      case UploadProgressState.success: {
        return <CloudDone className={classes.successIcon} />;
      }
      case UploadProgressState.failed: {
        return <ErrorOutline className={classes.failedIcon} />;
      }
      default:
        return <CloudUpload className={classes.defaultIcon} />;
    }
  }

  function renderProgress() {
    switch (state) {
      case UploadProgressState.idle:
      case UploadProgressState.processing: {
        return <span>&nbsp;</span>;
      }
      default:
        return progress !== undefined ? `${progress?.toFixed(0)}%` : '-';
    }
  }

  return (
    <Collapse in={state !== UploadProgressState.idle}>
      <Box
        className={className}
        width="100%"
        boxShadow={1}
        py={2}
        px={2}
        display="flex"
        alignItems="center"
        onClick={handleRootClick}
      >
        {renderIcon()}
        <Box ml={2} flexGrow={1}>
          <Typography variant="subtitle1" className={classes.progressText}>
            {renderProgress()}
          </Typography>
          <Box flexGrow={1} display="flex" alignItems="center" mb={-2}>
            <BorderLinearProgress state={state} progress={progress} />
            {state === UploadProgressState.failed ? (
              <IconButton onClick={handleClose} size="large">
                <Cancel />
              </IconButton>
            ) : (
              <Box width={46} height={46} />
            )}
          </Box>
          <Box display="flex" alignItems="flex-end">
            {state === UploadProgressState.failed && error && (
              <Typography
                variant="body2"
                color="error"
                className={classes.errorMessage}
              >
                {error}
              </Typography>
            )}
            <Typography variant="caption">{fileName}</Typography>
          </Box>
        </Box>
      </Box>
    </Collapse>
  );
};

UploadProgressIndicator.propTypes = {
  className: PropTypes.string,

  progress: PropTypes.number,
  fileName: PropTypes.string,
  error: PropTypes.string,

  state: PropTypes.oneOf([
    UploadProgressState.idle,
    UploadProgressState.processing,
    UploadProgressState.uploading,
    UploadProgressState.saving,
    UploadProgressState.success,
    UploadProgressState.failed,
  ]),

  onClose: PropTypes.func,
};

UploadProgressIndicator.defaultProps = {
  className: undefined,

  progress: undefined,
  fileName: '',
  error: undefined,
  state: UploadProgressState.idle,
  onClose() {},
};

export default UploadProgressIndicator;
